import { bboxLineWidth } from './Shapes/common';
import {
  type BBox,
  type DrawingCanvasSelectedFeature,
  type DrawingCanvasShape,
  type DrawingCanvasShapeKey,
  type DrawingCanvasShapes,
  DrawingCanvasShapeType,
  type Point,
  PointShape,
  type RGBColor,
} from './types';

export const scaleCanvasForHighResDisplay = (canvas: HTMLCanvasElement | null, width: number, height: number): void => {
  if (!canvas) return;

  const dpr = window.devicePixelRatio;

  // Set the "actual" size of the canvas
  canvas.width = width * dpr;
  canvas.height = height * dpr;

  // Set the "drawn" size of the canvas
  canvas.style.width = `${width}px`;
  canvas.style.height = `${height}px`;

  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  ctx.scale(dpr, dpr);
};

export const getCanvasCoordsFromEvent = (
  event: React.MouseEvent<HTMLCanvasElement>,
  canvas: HTMLCanvasElement,
  scale = 1,
): Point => {
  const rect = canvas.getBoundingClientRect();
  const { clientX, clientY } = event;
  const x = (clientX - rect.x) / scale;
  const y = (clientY - rect.y) / scale;
  return { x, y };
};

/** True for a key pressed without Meta, Ctrl or Alt, and not mid IME composition. */
export const isUnmodifiedKey = (event: React.KeyboardEvent): boolean =>
  !event.metaKey && !event.ctrlKey && !event.altKey && !event.nativeEvent.isComposing;

/** The order that draws above every existing shape. */
export const getNextOrder = (drawingShapes: DrawingCanvasShapes): number =>
  Math.max(-1, ...Object.values(drawingShapes).map((shape) => shape.order)) + 1;

/**
 * Maps an index to a unique `rgb(...)` color, skipping black (rgb(0,0,0)) because the empty canvas is black.
 * Colors for adjacent indexes are spaced far apart so smoothed edge pixels don't match a different shape.
 */
export const indexToColor = (index: number): RGBColor => {
  const stepSize = 4;
  const steps = 256 / stepSize;
  const maxColorIndex = steps ** 3 - 1;
  if (index >= maxColorIndex) throw new Error('Color index overflow');
  // Multiply index by a large number and then take that mod maxColorIndex to give adjacent indexes far apart colors.
  // Any large multiplier works if it has no common factors with `maxColorIndex`.
  const colorIndex = ((index * 180_659) % maxColorIndex) + 1;

  const red = Math.floor(colorIndex / steps ** 2) * stepSize;
  const blue = Math.floor((colorIndex % steps ** 2) / steps) * stepSize;
  const green = Math.floor(colorIndex % steps) * stepSize;

  return `rgb(${[red, blue, green].join(',')})`;
};

export const arrowKeySteps: Record<string, Point> = {
  ArrowUp: { x: 0, y: -1 },
  ArrowDown: { x: 0, y: 1 },
  ArrowLeft: { x: -1, y: 0 },
  ArrowRight: { x: 1, y: 0 },
};

export const getBBox = (points: Point[]): BBox => {
  const buffer = 20;
  const xVals = points.map((p) => p.x);
  const yVals = points.map((p) => p.y);

  const minX = Math.min(...xVals) - buffer;
  const maxX = Math.max(...xVals) + buffer;
  const minY = Math.min(...yVals) - buffer;
  const maxY = Math.max(...yVals) + buffer;

  return [
    { x: minX, y: minY },
    { x: minX, y: maxY },
    { x: maxX, y: maxY },
    { x: maxX, y: minY },
  ];
};

const getDistance = (a: Point, b: Point): number => Math.hypot(a.x - b.x, a.y - b.y);

export const isNearPoint = (a: Point, b: Point, radius: number): boolean => getDistance(a, b) <= radius;

const getNearestPointOnSegment = (start: Point, end: Point, point: Point): Point => {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const lengthSquared = dx ** 2 + dy ** 2;
  if (lengthSquared === 0) return start;
  // How far along the segment the point projects, from 0 (start) to 1 (end)
  const t = Math.min(1, Math.max(0, ((point.x - start.x) * dx + (point.y - start.y) * dy) / lengthSquared));
  return { x: start.x + t * dx, y: start.y + t * dy };
};

/**
 * Nearest point on a line to `point`, with the index of the segment it lies on.
 * Segment `i` runs from `points[i]` to `points[i + 1]`. Returns undefined for fewer than 2 points.
 */
export const getNearestPointOnLine = (
  points: Point[],
  point: Point,
): { segmentIndex: number; point: Point } | undefined => {
  let nearest: { segmentIndex: number; point: Point; distance: number } | undefined;
  for (let i = 0; i < points.length - 1; i++) {
    const candidate = getNearestPointOnSegment(points[i], points[i + 1], point);
    const distance = getDistance(candidate, point);
    if (!nearest || distance < nearest.distance) nearest = { segmentIndex: i, point: candidate, distance };
  }
  return nearest && { segmentIndex: nearest.segmentIndex, point: nearest.point };
};

/** Adds `point` between `points[segmentIndex]` and `points[segmentIndex + 1]`. */
export const insertVertex = (points: Point[], segmentIndex: number, point: Point): Point[] => [
  ...points.slice(0, segmentIndex + 1),
  point,
  ...points.slice(segmentIndex + 1),
];

export const moveVertex = (points: Point[], index: number, point: Point): Point[] =>
  points.map((vertex, i) => (i === index ? point : vertex));

export const removeVertex = (points: Point[], index: number): Point[] => points.filter((_, i) => i !== index);

/** Key for part of a selected shape, e.g. `getSelectedPartKey('1', 'vertex', 0)` is `1_selected_vertex_0`. */
export const getSelectedPartKey = (key: DrawingCanvasShapeKey, ...parts: (string | number)[]): DrawingCanvasShapeKey =>
  [key, 'selected', ...parts].join('_');

/** The bounding box and vertex features drawn for a selected shape, each in drawing order. */
export const getSelectedShapeFeatures = (
  shape: DrawingCanvasShape,
  selectedFeatureKey: DrawingCanvasShapeKey | undefined,
  hoveredFeatureKey?: DrawingCanvasShapeKey,
): {
  bBoxFeatures: Record<DrawingCanvasShapeKey, DrawingCanvasSelectedFeature>;
  vertexFeatures: Record<DrawingCanvasShapeKey, DrawingCanvasSelectedFeature>;
} => {
  const { key, order } = shape;
  const bBox = getBBox(shape.points);
  const bBoxKey = getSelectedPartKey(key, 'bbox');
  const bBoxFeatures: Record<DrawingCanvasShapeKey, DrawingCanvasSelectedFeature> = {
    [bBoxKey]: {
      key: bBoxKey,
      type: DrawingCanvasShapeType.BBOX,
      points: bBox,
      order,
      style: { fillColor: 'transparent', lineWidth: bboxLineWidth },
    },
  };
  bBox.forEach((point, index) => {
    const cornerKey = getSelectedPartKey(key, 'bbox', 'corner', index);
    bBoxFeatures[cornerKey] = {
      key: cornerKey,
      type: DrawingCanvasShapeType.BBOX_CORNER,
      points: [point],
      index,
      style: { pointShape: PointShape.SQUARE, lineWidth: bboxLineWidth },
      order,
    };
  });

  const vertexFeatures: Record<DrawingCanvasShapeKey, DrawingCanvasSelectedFeature> = {};
  if ([DrawingCanvasShapeType.LINE, DrawingCanvasShapeType.POLYGON].includes(shape.type)) {
    shape.points.forEach((point, index) => {
      const vertexKey = getSelectedPartKey(key, 'vertex', index);
      const isSelected = vertexKey === selectedFeatureKey;
      const isHighlighted = vertexKey === hoveredFeatureKey;
      vertexFeatures[vertexKey] = {
        key: vertexKey,
        type: DrawingCanvasShapeType.VERTEX,
        points: [point],
        index,
        style: isSelected || isHighlighted ? { isSelected, isHighlighted } : undefined,
        order,
      };
    });
  }
  return { bBoxFeatures, vertexFeatures };
};

export const drawingShapesSortFunc = (a: DrawingCanvasShape, b: DrawingCanvasShape): number => a.order - b.order;
